import {
  TranscriptionRequest,
  TranscriptionResponse,
  TaskListResponse,
  TranscriptionResult,
  ApiError,
  TranscriptFormat,
  SmsAnalytics,
  CommentAnalysisResponse,
  CommentAnalysisRequest,
  CommentPreviewResponse,
  CommentFetchRequest,
  CommentFetchResponse,
  CommentFetchStatus,
  UserAccount,
  TranscriptChatResponse,
  ChatThread,
  SendMessageResponse,
} from './types'
import { supabase } from './supabase'

const API_BASE_URL = 'https://api.scribetok.com'
// CORS proxy now requires activation at https://cors-anywhere.herokuapp.com/corsdemo
const CORS_PROXY = 'https://cors-anywhere.herokuapp.com/'
const CORS_DEMO_URL = 'https://cors-anywhere.herokuapp.com/corsdemo'

// Track if we've shown the CORS proxy activation message
let hasShownCorsProxyMessage = false

/**
 * Error handling for API responses with improved logging and retry mechanism
 */
const handleApiResponse = async <T,>(
  response: Response,
  retryCount = 0,
): Promise<T> => {
  if (!response.ok) {
    console.error(`API Error (${response.status}): ${response.statusText}`)

    // Special handling for CORS proxy 403 errors
    if (response.status === 403 && response.url.includes(CORS_PROXY)) {
      const errorText = await response.text()
      if (errorText.includes('/corsdemo')) {
        throw new Error(
          `CORS Proxy requires activation. Please visit ${CORS_DEMO_URL} in a new tab, click "Request temporary access", then try again.`,
        )
      }
    }

    // Special handling for 402 Payment Required (insufficient credits)
    if (response.status === 402) {
      try {
        const errorData = await response.json()
        const message =
          errorData.detail || errorData.message || 'Insufficient credits'
        throw new Error(message)
      } catch (e) {
        // If JSON parsing fails, provide a generic message
        throw new Error(
          'Insufficient credits. Please add more credits to continue.',
        )
      }
    }

    // Retry logic for 5xx errors (server errors) up to 3 times
    if (response.status >= 500 && response.status < 600 && retryCount < 3) {
      console.log(`Retrying request (attempt ${retryCount + 1})...`)
      // Wait with exponential backoff
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * Math.pow(2, retryCount)),
      )
      // Create retry options - but don't include body for GET/HEAD requests
      const method = response.method || 'GET'
      const retryOptions: RequestInit = {
        method,
        headers: response.headers,
      }
      // Only add body for non-GET/HEAD requests to prevent the browser error
      if (method !== 'GET' && method !== 'HEAD' && !response.bodyUsed) {
        try {
          retryOptions.body = await response.clone().text()
        } catch (e) {
          console.warn('Could not clone response body for retry', e)
        }
      }
      // Recreate the request and retry
      const retryResponse = await fetch(response.url, retryOptions)
      return handleApiResponse<T>(retryResponse, retryCount + 1)
    }

    // Check content type to handle non-JSON responses
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      try {
        // Clone the response before reading to avoid consuming the stream
        const errorData: ApiError = await response.clone().json()
        const errorMessage =
          errorData.detail?.[0]?.msg ||
          errorData.detail ||
          `API Error: ${response.status}`
        throw new Error(errorMessage)
      } catch (e) {
        // If JSON parsing fails, try to get text
        try {
          const errorText = await response.text()
          throw new Error(`API Error: ${response.status} - ${errorText}`)
        } catch (textError) {
          throw new Error(`API Error: ${response.status}`)
        }
      }
    } else {
      // Handle non-JSON responses (text, HTML, etc.)
      try {
        const errorText = await response.text()
        throw new Error(`API Error: ${response.status} - ${errorText}`)
      } catch (e) {
        throw new Error(`API Error: ${response.status}`)
      }
    }
  }

  // Check if response is JSON before parsing
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return await response.json()
  } else {
    // For text responses (like plain transcript text)
    const text = await response.text()
    // This handles cases where the API returns plain text but the caller expects JSON
    return text as unknown as T
  }
}

/**
 * Helper function to get authentication headers from Supabase session
 */
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {}
  // Always include the Supabase anon key for public endpoints
  const supabaseAnonKey =
    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVva3RzYXJvY2thdmpkc3hxaGptIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MDcwMjc2MzksImV4cCI6MjAyMjYwMzYzOX0.eBtxNzxttv8KE5nG1YnZ9ELB0VCEyVJXlv0Rjr1v2wk'
  headers['X-API-Key'] = supabaseAnonKey
  try {
    // Try to get authenticated session token which will override the anon key if available
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (session?.access_token) {
      headers['X-API-Key'] = session.access_token
    }
  } catch (error) {
    console.log('No auth session available, using anon key')
  }
  return headers
}

/**
 * Helper function to fetch with fallback to CORS proxy if direct request fails
 * Shows a user-friendly message when CORS proxy needs activation
 */
const fetchWithCorsProxy = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  // Ensure we don't send a body with GET/HEAD requests
  const method = options.method || 'GET'
  if ((method === 'GET' || method === 'HEAD') && options.body) {
    console.warn('Removing body from GET/HEAD request to prevent browser error')
    const newOptions = { ...options }
    delete newOptions.body
    options = newOptions
  }

  // IMPORTANT: Preserve method for POST requests
  // Do NOT convert POST to GET during retries
  if (method === 'POST' && !options.body) {
    console.error('POST request missing body - this will likely fail')
  }

  // Add authentication headers if available
  const authHeaders = await getAuthHeaders()
  options.headers = {
    ...options.headers,
    ...authHeaders,
  }

  try {
    // First try direct request
    console.log(`Making ${method} request to: ${url}`)
    const directResponse = await fetch(url, options)

    // If successful, return the response
    if (directResponse.ok) {
      console.log(`Direct API request successful (${directResponse.status})`)
      return directResponse
    }

    // Log non-OK responses for debugging
    console.warn(
      `Direct request returned ${directResponse.status}: ${directResponse.statusText}`,
    )

    // If we got a response but it's not OK, check if it's a CORS error
    if (directResponse.status === 0 || directResponse.type === 'opaque') {
      console.warn('Possible CORS issue with direct request, trying proxy')
    }

    // If not a CORS error, return the original error response
    if (directResponse.status !== 0) {
      return directResponse
    }

    // Try with CORS proxy as fallback
    console.warn('Direct API request failed, trying with CORS proxy')
    const proxyUrl = `${CORS_PROXY}${url}`
    console.log(`Trying with CORS proxy: ${proxyUrl}`)

    try {
      // IMPORTANT: Preserve original method and body for proxy request
      const proxyResponse = await fetch(proxyUrl, options)

      // If proxy returns 403 and contains '/corsdemo', it needs activation
      if (proxyResponse.status === 403) {
        const text = await proxyResponse.clone().text()
        if (text.includes('/corsdemo') && !hasShownCorsProxyMessage) {
          hasShownCorsProxyMessage = true
          console.error('CORS proxy needs activation')
          // Create a more user-friendly response
          return new Response(
            JSON.stringify({
              error: 'CORS_PROXY_NEEDS_ACTIVATION',
              message: `The CORS proxy requires activation. Please visit ${CORS_DEMO_URL} in a new tab, click "Request temporary access", then try again.`,
            }),
            {
              status: 403,
              headers: { 'Content-Type': 'application/json' },
            },
          )
        }
      }

      return proxyResponse
    } catch (proxyError) {
      console.error('CORS proxy request failed:', proxyError)
      // If both direct and proxy fail, return a custom error response
      return new Response(
        JSON.stringify({
          error: 'CONNECTION_FAILED',
          message:
            'Unable to connect to the API. Please check your internet connection and try again.',
        }),
        {
          status: 500,
          headers: { 'Content-Type': 'application/json' },
        },
      )
    }
  } catch (error) {
    console.warn('Direct API request failed:', error)
    // Try with CORS proxy as fallback
    const proxyUrl = `${CORS_PROXY}${url}`
    console.log(`Trying with CORS proxy: ${proxyUrl}`)

    try {
      // IMPORTANT: Preserve original method and body for proxy request
      return await fetch(proxyUrl, options)
    } catch (proxyError) {
      console.error('CORS proxy request also failed:', proxyError)
      // If both direct and proxy fail, return a custom error response
      return new Response(
        JSON.stringify({
          error: 'CONNECTION_FAILED',
          message:
            'Unable to connect to the API. Please check your internet connection and try again.',
        }),
        {
          status: 500,
          headers: { 'Content-Type': 'application/json' },
        },
      )
    }
  }
}

/**
 * Start a new transcription job with enhanced options
 */
export async function transcribeVideo(
  request: TranscriptionRequest,
): Promise<TranscriptionResponse> {
  try {
    console.log('Sending transcription request to API:', request)
    // Create a more complete request object with all supported parameters
    const apiRequest: TranscriptionRequest = {
      url: request.url,
      callback_url: request.callback_url || null,
      format: request.format || 'bestaudio/best',
      extract_audio:
        request.extract_audio !== undefined ? request.extract_audio : true,
      convert_to_mp3: request.convert_to_mp3 || false,
      save_thumbnail:
        request.save_thumbnail !== undefined ? request.save_thumbnail : true,
      extract_metadata:
        request.extract_metadata !== undefined
          ? request.extract_metadata
          : true,
      perform_sentiment_analysis: request.perform_sentiment_analysis || false,
      create_srt: request.create_srt || false,
      proxy: request.proxy || null,
      user_phone: request.user_phone || null,
    }
    // Try direct request first, fall back to CORS proxy if needed
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/transcribe`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiRequest),
      },
    )
    const data = await handleApiResponse<TranscriptionResponse>(response)
    console.log('API transcription response:', data)
    // Store in Supabase if user is authenticated
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      // Store transcription in Supabase
      const { error } = await supabase.from('transcriptions').insert({
        task_id: data.task_id,
        user_id: user.id,
        title: data.title,
        video_id: data.video_id,
        status: data.status,
        thumbnail_url: data.thumbnail_url || data.thumbnail,
      })
      if (error) {
        console.error('Error storing transcription in Supabase:', error)
      }
    }
    return data
  } catch (error) {
    console.error('Error in transcribeVideo:', error)
    throw error
  }
}

/**
 * Get status of a transcription task
 */
export const getTaskStatus = async (
  taskId: string,
): Promise<TranscriptionResponse> => {
  try {
    console.log(`Checking status for task: ${taskId}`)
    // First try to get from Supabase
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      const { data: transcription } = await supabase
        .from('transcriptions')
        .select('*')
        .eq('task_id', taskId)
        .single()
      if (transcription) {
        // If status is not completed or failed, check latest status from API
        if (
          transcription.status !== 'completed' &&
          transcription.status !== 'failed'
        ) {
          try {
            // Try direct request first, fall back to CORS proxy if needed
            const response = await fetchWithCorsProxy(
              `${API_BASE_URL}/api/public/tasks/${taskId}`,
            )
            const apiData =
              await handleApiResponse<TranscriptionResponse>(response)
            console.log(`Updated status for task ${taskId}: ${apiData.status}`)
            // Update status in Supabase
            if (apiData.status !== transcription.status) {
              await supabase
                .from('transcriptions')
                .update({
                  status: apiData.status,
                  supabase_thumbnail_url: apiData.supabase_thumbnail_url,
                  square_thumbnail_url: apiData.square_thumbnail_url,
                  quote: apiData.quote,
                  tldr: apiData.tldr,
                })
                .eq('task_id', taskId)
            }
            return apiData
          } catch (error) {
            console.error('Error getting task status from API:', error)
            // Return the Supabase data if API call fails
            return {
              task_id: transcription.task_id,
              status: transcription.status,
              video_id: transcription.video_id,
              title: transcription.title,
              created_at: transcription.created_at,
              error: null,
              thumbnail: transcription.thumbnail_url,
              supabase_thumbnail_url: transcription.supabase_thumbnail_url,
              square_thumbnail_url: transcription.square_thumbnail_url,
              quote: transcription.quote,
              tldr: transcription.tldr,
            }
          }
        }
        return {
          task_id: transcription.task_id,
          status: transcription.status,
          video_id: transcription.video_id,
          title: transcription.title,
          created_at: transcription.created_at,
          error: null,
          thumbnail: transcription.thumbnail_url,
          supabase_thumbnail_url: transcription.supabase_thumbnail_url,
          square_thumbnail_url: transcription.square_thumbnail_url,
          quote: transcription.quote,
          tldr: transcription.tldr,
        }
      }
    }
    // Fallback to API if not found in Supabase
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/tasks/${taskId}`,
    )
    const apiData = await handleApiResponse<TranscriptionResponse>(response)
    console.log(`Retrieved status for task ${taskId}: ${apiData.status}`)
    // Store in Supabase if user is authenticated
    if (user) {
      const { error } = await supabase.from('transcriptions').upsert({
        task_id: apiData.task_id,
        user_id: user.id,
        title: apiData.title,
        video_id: apiData.video_id,
        status: apiData.status,
        thumbnail_url: apiData.thumbnail_url,
        supabase_thumbnail_url: apiData.supabase_thumbnail_url,
        square_thumbnail_url: apiData.square_thumbnail_url,
        quote: apiData.quote,
        tldr: apiData.tldr,
      })
      if (error) {
        console.error('Error storing transcription in Supabase:', error)
      }
    }
    return apiData
  } catch (error) {
    console.error('Error getting task status:', error)
    throw error
  }
}

/**
 * Get all transcription tasks
 */
export const getAllTasks = async (): Promise<TaskListResponse> => {
  try {
    // First try to get from Supabase if user is authenticated
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      const { data: transcriptions, error } = await supabase
        .from('transcriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      if (error) {
        console.error('Error getting transcriptions from Supabase:', error)
      } else if (transcriptions && transcriptions.length > 0) {
        return {
          tasks: transcriptions.map((t) => ({
            task_id: t.task_id,
            status: t.status,
            video_id: t.video_id,
            title: t.title,
            created_at: t.created_at,
            error: null,
            thumbnail: t.thumbnail_url,
            supabase_thumbnail_url: t.supabase_thumbnail_url,
            square_thumbnail_url: t.square_thumbnail_url,
            quote: t.quote,
            tldr: t.tldr,
          })),
        }
      }
    }
    // Fallback to API if not found in Supabase
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/tasks`,
    )
    const apiData = await handleApiResponse<TaskListResponse>(response)
    // Store in Supabase if user is authenticated
    if (user && apiData.tasks.length > 0) {
      const supabaseTasks = apiData.tasks.map((task) => ({
        task_id: task.task_id,
        user_id: user.id,
        title: task.title,
        video_id: task.video_id,
        status: task.status,
        thumbnail_url: task.thumbnail_url,
        supabase_thumbnail_url: task.supabase_thumbnail_url,
        square_thumbnail_url: task.square_thumbnail_url,
        quote: task.quote,
        tldr: task.tldr,
      }))
      const { error } = await supabase
        .from('transcriptions')
        .upsert(supabaseTasks)
      if (error) {
        console.error('Error storing transcriptions in Supabase:', error)
      }
    }
    return apiData
  } catch (error) {
    console.error('Error getting all tasks:', error)
    throw error
  }
}

/**
 * Get transcript for a completed task with format options
 */
export const getTranscript = async (taskId: string): Promise<string> => {
  try {
    // First try to get from Supabase
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      const { data: transcription } = await supabase
        .from('transcriptions')
        .select('*')
        .eq('task_id', taskId)
        .single()
      if (transcription && transcription.transcript) {
        return transcription.transcript
      }
    }
    // Fallback to API if not found in Supabase
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/transcript/${taskId}`,
    )
    const transcript = await handleApiResponse<string>(response)
    // Store in Supabase if user is authenticated
    if (user) {
      const { error } = await supabase
        .from('transcriptions')
        .update({ transcript })
        .eq('task_id', taskId)
      if (error) {
        console.error('Error storing transcript in Supabase:', error)
      }
    }
    return transcript
  } catch (error) {
    console.error('Error getting transcript:', error)
    throw error
  }
}

/**
 * Get transcript with specified format (text, json, srt)
 */
export const getTranscriptWithFormat = async (
  taskId: string,
  format: TranscriptFormat = 'text',
): Promise<string | TranscriptionResult> => {
  try {
    // First try to get from Supabase if format is text
    if (format === 'text') {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (user) {
        const { data: transcription } = await supabase
          .from('transcriptions')
          .select('*')
          .eq('task_id', taskId)
          .single()
        if (transcription && transcription.transcript) {
          return transcription.transcript
        }
      }
    }
    // Get from API with specified format
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/transcript/${taskId}?format=${format}`,
    )
    if (format === 'json') {
      return handleApiResponse<TranscriptionResult>(response)
    } else {
      const transcript = await handleApiResponse<string>(response)
      // Store in Supabase if format is text and user is authenticated
      if (format === 'text') {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (user) {
          const { error } = await supabase
            .from('transcriptions')
            .update({ transcript })
            .eq('task_id', taskId)
          if (error) {
            console.error('Error storing transcript in Supabase:', error)
          }
        }
      }
      return transcript
    }
  } catch (error) {
    console.error(`Error getting transcript in ${format} format:`, error)
    throw error
  }
}

/**
 * Delete a task and its associated files
 */
export const deleteTask = async (taskId: string): Promise<string> => {
  try {
    // Delete from Supabase first
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      const { error } = await supabase
        .from('transcriptions')
        .delete()
        .eq('task_id', taskId)
        .eq('user_id', user.id)
      if (error) {
        console.error('Error deleting transcription from Supabase:', error)
      }
    }
    // Then delete from API
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/tasks/${taskId}`,
      {
        method: 'DELETE',
      },
    )
    return handleApiResponse<string>(response)
  } catch (error) {
    console.error('Error deleting task:', error)
    throw error
  }
}

/**
 * Check API health
 */
export const checkApiHealth = async (): Promise<string> => {
  try {
    // Use the healthcheck endpoint
    const response = await fetchWithCorsProxy(`${API_BASE_URL}/api/healthcheck`)
    return handleApiResponse<string>(response)
  } catch (error) {
    console.error('Error checking API health:', error)
    throw error
  }
}

/**
 * Parse a raw transcript into segments
 * This is a helper function to convert the raw transcript text into structured segments
 */
export const parseTranscript = (rawTranscript: string): TranscriptionResult => {
  // This is a simplified example - in a real app, you'd parse the actual transcript format
  const text = rawTranscript
  // Check if the transcript contains timestamp markers like "0:00 - 0:05"
  const hasTimestamps = /\d+:\d+\s*-\s*\d+:\d+/.test(rawTranscript)
  let segments: TranscriptionSegment[] = []
  if (hasTimestamps) {
    // Parse transcript with timestamps
    // Match segments with pattern like "0:00 - 0:05 Text content here"
    const segmentMatches = rawTranscript.match(
      /(\d+:\d+\s*-\s*\d+:\d+)([^]*?)(?=\d+:\d+\s*-\s*\d+:\d+|$)/g,
    )
    if (segmentMatches) {
      segments = segmentMatches.map((segment, index) => {
        const timestampMatch = segment.match(/(\d+):(\d+)\s*-\s*(\d+):(\d+)/)
        let start = 0
        let end = 5
        if (timestampMatch) {
          const startMins = parseInt(timestampMatch[1])
          const startSecs = parseInt(timestampMatch[2])
          const endMins = parseInt(timestampMatch[3])
          const endSecs = parseInt(timestampMatch[4])
          start = startMins * 60 + startSecs
          end = endMins * 60 + endSecs
        }
        // Extract the text part after the timestamp
        const textMatch = segment.match(/\d+:\d+\s*-\s*\d+:\d+\s*(.*)/s)
        const segmentText = textMatch ? textMatch[1].trim() : segment.trim()
        return {
          id: index + 1,
          start,
          end,
          text: segmentText,
        }
      })
    }
  } else {
    // Fallback for transcripts without timestamps
    // Split by paragraphs or sentences
    const paragraphs = text
      .split(/\n\n|\.\s+(?=[A-Z])/)
      .filter((p) => p.trim().length > 0)
    segments = paragraphs.map((paragraph, index) => {
      // Create artificial timestamps (15 seconds per segment)
      const start = index * 15
      const end = start + 15
      return {
        id: index + 1,
        start,
        end,
        text: paragraph.trim(),
      }
    })
  }
  return { segments, text }
}

// Discovery API Endpoints

/**
 * Get trending public transcriptions
 */
export const getTrendingTranscriptions = async (
  timeWindow: 'week' | 'month' | 'all' = 'week',
  category?: string,
  limit: number = 10,
): Promise<TranscriptionResponse[]> => {
  try {
    const params = new URLSearchParams()
    if (timeWindow) params.append('time_window', timeWindow)
    if (category) params.append('category', category)
    params.append('limit', limit.toString())
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/discover/trending?${params.toString()}`,
    )
    return handleApiResponse<TranscriptionResponse[]>(response)
  } catch (error) {
    console.error('Error getting trending transcriptions:', error)
    throw error
  }
}

/**
 * Get similar transcriptions based on tags and category
 */
export const getSimilarTranscriptions = async (
  taskId: string,
  limit: number = 5,
): Promise<TranscriptionResponse[]> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/discover/similar/${taskId}?limit=${limit}`,
    )
    return handleApiResponse<TranscriptionResponse[]>(response)
  } catch (error) {
    console.error('Error getting similar transcriptions:', error)
    throw error
  }
}

/**
 * Get recently added public transcriptions
 */
export const getRecentTranscriptions = async (
  category?: string,
  limit: number = 10,
): Promise<TranscriptionResponse[]> => {
  try {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    params.append('limit', limit.toString())
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/discover/recent?${params.toString()}`,
    )
    return handleApiResponse<TranscriptionResponse[]>(response)
  } catch (error) {
    console.error('Error getting recent transcriptions:', error)
    throw error
  }
}

/**
 * Get list of available categories
 */
export const getCategories = async (): Promise<string[]> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/discover/categories`,
    )
    return handleApiResponse<string[]>(response)
  } catch (error) {
    console.error('Error getting categories:', error)
    throw error
  }
}

/**
 * Get public transcript page URL
 */
export const getPublicTranscriptPageUrl = (
  taskId: string,
  ref?: string,
): string => {
  const baseUrl = `https://share.scribetok.com/v/${taskId}`
  return ref ? `${baseUrl}?ref=${encodeURIComponent(ref)}` : baseUrl
}

/**
 * Search transcriptions with full-text search
 */
export const searchTranscriptions = async (
  query: string,
  limit: number = 10,
  offset: number = 0,
): Promise<TranscriptionResponse[]> => {
  try {
    const params = new URLSearchParams()
    params.append('q', query)
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/discover/search?${params.toString()}`,
    )
    return handleApiResponse<TranscriptionResponse[]>(response)
  } catch (error) {
    console.error('Error searching transcriptions:', error)
    throw error
  }
}

/**
 * Search viral content with engagement filters
 */
export const searchViralContent = async (
  query: string,
  minLikes: number = 0,
  minViews: number = 0,
  limit: number = 10,
): Promise<TranscriptionResponse[]> => {
  try {
    const params = new URLSearchParams()
    params.append('q', query)
    if (minLikes > 0) params.append('min_likes', minLikes.toString())
    if (minViews > 0) params.append('min_views', minViews.toString())
    params.append('limit', limit.toString())
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/discover/viral?${params.toString()}`,
    )
    return handleApiResponse<TranscriptionResponse[]>(response)
  } catch (error) {
    console.error('Error searching viral content:', error)
    throw error
  }
}

/**
 * SMS Integration Functions
 */

/**
 * Send an SMS message
 */
export const sendSms = async (
  to: string,
  message: string,
  apiKey: string,
): Promise<string> => {
  try {
    const response = await fetchWithCorsProxy(`${API_BASE_URL}/api/sms/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      body: JSON.stringify({ to, message }),
    })
    return handleApiResponse<string>(response)
  } catch (error) {
    console.error('Error sending SMS:', error)
    throw error
  }
}

/**
 * Get SMS usage analytics
 */
export const getSmsAnalytics = async (
  apiKey: string,
  timeWindow: 'day' | 'week' | 'month' = 'week',
): Promise<SmsAnalytics> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/analytics/sms?time_window=${timeWindow}`,
      {
        headers: {
          'X-API-Key': apiKey,
        },
      },
    )
    return handleApiResponse<SmsAnalytics>(response)
  } catch (error) {
    console.error('Error getting SMS analytics:', error)
    throw error
  }
}

// Add this function to handle plan upgrades with Supabase
export const upgradeToPro = async (): Promise<UserAccount> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }
    // Update the user's profile to Pro
    const { error } = await supabase
      .from('profiles')
      .update({
        is_pro: true,
        usage_limit: 500,
      })
      .eq('id', user.id)
    if (error) {
      throw new Error(error.message)
    }
    // Get the updated profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()
    if (profileError) {
      throw new Error(profileError.message)
    }
    if (!profile) {
      throw new Error('Profile not found')
    }
    // Return the updated user account
    return {
      email: profile.email,
      plan: {
        name: 'Pro Plan',
        minutesLimit: profile.usage_limit,
        isPro: true,
      },
      usage: {
        minutesUsed: profile.usage_minutes,
        percentage: (profile.usage_minutes / profile.usage_limit) * 100,
      },
    }
  } catch (error) {
    console.error('Error upgrading to Pro:', error)
    throw error
  }
}

// Replace the getUserAccount function to use Supabase
export const getUserAccount = async (): Promise<UserAccount> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }
    // Get the user's profile
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()
    if (error) {
      throw new Error(error.message)
    }
    if (!profile) {
      throw new Error('Profile not found')
    }
    // Return the user account
    return {
      email: profile.email,
      plan: {
        name: profile.is_pro ? 'Pro Plan' : 'Free Plan',
        minutesLimit: profile.usage_limit,
        isPro: profile.is_pro,
      },
      usage: {
        minutesUsed: profile.usage_minutes,
        percentage: (profile.usage_minutes / profile.usage_limit) * 100,
      },
    }
  } catch (error) {
    console.error('Error getting user account:', error)
    throw error
  }
}

/**
 * Stop a transcription task
 */
export const stopTranscription = async (taskId: string): Promise<boolean> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/tasks/${taskId}/stop`,
      {
        method: 'POST',
      },
    )
    await handleApiResponse<any>(response)
    // Update status in Supabase if user is authenticated
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      const { error } = await supabase
        .from('transcriptions')
        .update({ status: 'cancelled' })
        .eq('task_id', taskId)
      if (error) {
        console.error('Error updating transcription status in Supabase:', error)
      }
    }
    return true
  } catch (error) {
    console.error('Error stopping transcription:', error)
    throw error
  }
}

/**
 * Retry a failed transcription task
 */
export const retryTask = async (
  taskId: string,
): Promise<TranscriptionResponse> => {
  try {
    console.log(`Retrying task: ${taskId}`)
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/tasks/${taskId}/retry`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    const data = await handleApiResponse<TranscriptionResponse>(response)
    console.log(`Task ${taskId} queued for retry:`, data)

    // Update status in Supabase if user is authenticated
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (user) {
      const { error } = await supabase
        .from('transcriptions')
        .update({ status: 'pending' })
        .eq('task_id', taskId)
      if (error) {
        console.error('Error updating transcription status in Supabase:', error)
      }
    }

    return data
  } catch (error) {
    console.error('Error retrying task:', error)
    throw error
  }
}

/**
 * Enhanced Comment Analysis API Functions with Preview & Progress
 */

/**
 * Get comment preview (FREE - no credits charged)
 * Shows sample comments and estimated credit cost
 */
export const getCommentPreview = async (
  taskId: string,
): Promise<CommentPreviewResponse> => {
  try {
    console.log(`Fetching comment preview for task: ${taskId}`)

    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/comments/preview/${taskId}`,
    )

    const data = await handleApiResponse<CommentPreviewResponse>(response)
    console.log(
      `Retrieved preview with ${data.preview_comments.length} comments. Estimated total: ${data.estimated_total}`,
    )

    return data
  } catch (error) {
    console.error('Error fetching comment preview:', error)
    throw error
  }
}

/**
 * Initiate comment fetch with pagination
 * This starts the fetch process and returns immediately
 *
 * IMPORTANT: Backend requires JSON body with task_id, get_all, and count
 */
export const initiateCommentFetch = async (
  request: CommentFetchRequest,
): Promise<CommentFetchResponse> => {
  try {
    const { task_id, get_all = true } = request

    console.log(`Initiating comment fetch for task: ${task_id}`)

    // Backend requires specific JSON body format
    const requestBody = {
      task_id: task_id,
      get_all: get_all,
      count: 30, // Default count for pagination
    }

    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/pro/comments/fetch`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      },
    )

    const data = await handleApiResponse<CommentFetchResponse>(response)
    console.log(`Comment fetch initiated: ${data.message}`)

    return data
  } catch (error) {
    console.error('Error initiating comment fetch:', error)
    throw error
  }
}

/**
 * Get real-time progress of comment fetching
 * Poll this endpoint to track progress
 */
export const getCommentFetchStatus = async (
  taskId: string,
): Promise<CommentFetchStatus> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/comments/fetch-status/${taskId}`,
    )

    const data = await handleApiResponse<CommentFetchStatus>(response)
    return data
  } catch (error) {
    console.error('Error getting comment fetch status:', error)
    throw error
  }
}

/**
 * Fetch comment analytics (legacy endpoint - still supported)
 * Use this for immediate results without progress tracking
 */
export const getCommentAnalytics = async (
  request: CommentAnalysisRequest,
): Promise<CommentAnalysisResponse> => {
  try {
    const { task_id, limit = 50 } = request

    console.log(`Fetching comment analytics for task: ${task_id}`)

    const params = new URLSearchParams()
    params.append('limit', limit.toString())

    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/comments/${task_id}/analytics?${params.toString()}`,
    )

    const data = await handleApiResponse<CommentAnalysisResponse>(response)
    console.log(
      `Retrieved analytics with ${data.top_comments.length} top comments for task ${task_id}`,
    )

    // Store comment data in Supabase if user is authenticated
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (user) {
      const { error } = await supabase.from('comment_analyses').insert({
        task_id: task_id,
        user_id: user.id,
        total_comments: data.total_comments,
        avg_likes: data.avg_likes,
        engagement_rate: data.engagement_rate,
        top_sentiment: `${data.sentiment.dominant} (${data.sentiment.positive}%)`,
        fetched_at: new Date().toISOString(),
      })

      if (error) {
        console.error('Error storing comment analysis in Supabase:', error)
      }
    }

    return data
  } catch (error) {
    console.error('Error fetching comment analytics:', error)
    throw error
  }
}

// Keep backward compatibility
export const getComments = getCommentAnalytics

/**
 * Export comments as CSV
 */
export const exportCommentsCSV = async (taskId: string): Promise<void> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/comments/${taskId}/export/csv`,
    )

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `comments-${taskId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting comments as CSV:', error)
    throw error
  }
}

/**
 * Export comments as JSON
 */
export const exportCommentsJSON = async (taskId: string): Promise<void> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/comments/${taskId}/export/json`,
    )

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `comments-${taskId}.json`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error exporting comments as JSON:', error)
    throw error
  }
}

/**
 * Check if comments have already been fetched for a task
 */
export const hasCommentData = async (taskId: string): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) return false

    const { data, error } = await supabase
      .from('comment_analyses')
      .select('id')
      .eq('task_id', taskId)
      .eq('user_id', user.id)
      .single()

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking comment data:', error)
    }

    return !!data
  } catch (error) {
    console.error('Error checking comment data:', error)
    return false
  }
}

/**
 * Get user's credit balance from sms_users table
 */
export const getCreditBalance = async (): Promise<number> => {
  try {
    // Get current user email from Supabase
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user?.email) {
      console.error('No user email found')
      return 0
    }

    // Query sms_users table by email
    const { data, error } = await supabase
      .from('sms_users')
      .select('credits_remaining')
      .eq('email', user.email)
      .single()

    if (error) {
      console.error('Error getting credit balance:', error)
      return 0
    }

    return data?.credits_remaining || 0
  } catch (error) {
    console.error('Error getting credit balance:', error)
    return 0
  }
}

/**
 * Get transaction history
 */
export const getTransactionHistory = async (): Promise<any[]> => {
  try {
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/users/transactions`,
    )

    return handleApiResponse<any[]>(response)
  } catch (error) {
    console.error('Error getting transaction history:', error)
    return []
  }
}

/**
 * Chat with a transcript - AI-powered Q&A
 */
export const chatWithTranscript = async (
  taskId: string,
  message: string,
): Promise<TranscriptChatResponse> => {
  try {
    console.log(`Sending chat message for task: ${taskId}`)

    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/public/transcript/${taskId}/chat`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      },
    )

    const data = await handleApiResponse<TranscriptChatResponse>(response)
    console.log(`Received chat response for task ${taskId}`)

    return data
  } catch (error) {
    console.error('Error chatting with transcript:', error)
    throw error
  }
}

/**
 * Web Chat API Functions
 */

/**
 * Get or create a chat thread for a specific transcript task
 * This is used when a user clicks the chat link from SMS
 */
export const getOrCreateThread = async (
  taskId: string,
): Promise<ChatThread> => {
  try {
    console.log(`Getting/creating chat thread for task: ${taskId}`)
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/chat/task/${taskId}`,
    )
    return handleApiResponse<ChatThread>(response)
  } catch (error) {
    console.error('Error getting chat thread:', error)
    throw error
  }
}

/**
 * Send a message to continue the conversation
 */
export const sendChatMessage = async (
  threadId: string,
  message: string,
): Promise<SendMessageResponse> => {
  try {
    console.log(`Sending message to thread: ${threadId}`)
    const response = await fetchWithCorsProxy(
      `${API_BASE_URL}/api/chat/thread/${threadId}/message`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          max_chars: 1500, // Allow longer responses on web
        }),
      },
    )
    return handleApiResponse<SendMessageResponse>(response)
  } catch (error) {
    console.error('Error sending chat message:', error)
    throw error
  }
}
